import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { T, pick } from "../lib/translations";
import { getCityBySlug } from "../lib/cities";
import { ALL_CITIES, ALL_GUIDES, postMatchesCity } from "../lib/relatedContent";
import { getPosts, getBootPosts } from "../lib/blogCache";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { ArrowRight, Phone, Scale, MapPin, Landmark, Check, BookOpen, Calendar, Clock } from "lucide-react";

const SITE_URL = "https://myrihai.com";

const decodeHtml = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

export default function CityPage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { lang } = useLang();
  const slug = loc.pathname.replace(/^\//, "").replace(/\/$/, "");
  const c = getCityBySlug(slug);
  const [posts, setPosts] = useState(() => getBootPosts() || []);

  useEffect(() => {
    getPosts().then(setPosts);
  }, []);

  if (!c) {
    return (
      <>
        <SEO title="City not found" path={`/${slug}`} />
        <Nav />
        <main className="pt-40 pb-20 min-h-screen container-x">
          <h1 className="font-serif-display text-4xl mb-4">City not found</h1>
          <button onClick={() => navigate("/")} className="btn-primary">Go home</button>
        </main>
        <Footer />
      </>
    );
  }

  const isHi = lang === "hi";
  const cityName = isHi ? c.cityHindi : c.city;
  const courtName = isHi ? c.highCourtHindi : c.highCourt;

  // Journal posts about this city first, then latest — internal links back to /blog
  const relatedPosts = [
    ...posts.filter((p) => postMatchesCity(p, c.slug)),
    ...posts.filter((p) => !postMatchesCity(p, c.slug)),
  ].slice(0, 3);

  // LegalService + LocalBusiness JSON-LD scoped to this city
  const legalServiceLD = {
    "@context": "https://schema.org",
    "@type": ["LegalService", "LocalBusiness"],
    name: `Rihai Bail Lawyer Network - ${c.city}`,
    url: `${SITE_URL}/${c.slug}`,
    telephone: "+919140650818",
    description: c.metaDescription,
    priceRange: "Flat transparent fee",
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    areaServed: {
      "@type": "AdministrativeArea",
      name: c.jurisdiction,
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: c.city,
      addressRegion: c.jurisdiction,
      addressCountry: "IN",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
        description: "Emergency bail support available 24/7",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+919140650818",
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["en-IN", "hi-IN"],
      },
    ],
    knowsAbout: [
      "Regular bail", "Anticipatory bail", "Interim bail",
      c.highCourt, ...c.subordinateCourts,
    ],
    inLanguage: ["en-IN", "hi-IN"],
    serviceType: "Bail lawyer connection",
  };

  return (
    <>
      <SEO
        title={isHi ? c.metaTitleHindi : `Emergency Bail Lawyers in ${c.city} | ${c.highCourt}`}
        titleSuffix=" | Rihai"
        description={isHi ? c.metaDescriptionHindi : c.metaDescription}
        path={`/${c.slug}`}
        type="website"
      />
      {/* Extra JSON-LD for this specific city — inside Helmet to hoist to <head> */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(legalServiceLD)}
        </script>
        {/* FAQPage JSON-LD from local FAQs — Google rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.localFAQ.map((f) => ({
              "@type": "Question",
              name: isHi ? f.qHi : f.q,
              acceptedAnswer: { "@type": "Answer", text: isHi ? f.aHi : f.a },
            })),
          })}
        </script>
        {/* BreadcrumbList — Home > City */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: `Bail Lawyers in ${c.city}`, item: `${SITE_URL}/${c.slug}` },
            ],
          })}
        </script>
      </Helmet>

      <Nav />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden" data-testid="city-hero">
        <div className="blob" style={{ width: 460, height: 460, background: "#D8CDBA", top: -140, right: -120 }} />
        <div className="container-x relative">
          <div className="flex items-center gap-2 mb-5">
            <MapPin size={14} className="text-[#C46B2F]" />
            <span className="overline">{isHi ? `भारत · ${cityName}` : `India · ${cityName}`}</span>
          </div>
          <h1 className="font-serif-display serif-textured text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-light text-[#1A1A1A] mb-6 max-w-4xl" data-testid="city-h1">
            {isHi ? c.heroH1Hindi : c.heroH1}
          </h1>
          <p className="text-lg sm:text-xl text-[#4A4640] max-w-3xl leading-relaxed mb-10" data-testid="city-sub">
            {isHi ? c.heroSubHindi : c.heroSub}
          </p>
          <div className="flex flex-wrap gap-4 mb-10">
            <Link to="/#lead" className="btn-primary" data-testid="city-cta-primary">
              {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${T.phone.number}`}
              className="btn-secondary border-[#A83F32] text-[#A83F32] hover:bg-[#A83F32] hover:text-white"
              data-testid="city-cta-call"
            >
              <Phone size={16} strokeWidth={2} /> {pick(T.hero.ctaCall, lang)} — {T.phone.display}
            </a>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#736F66]">
            {[T.hero.trust1, T.hero.trust2, T.hero.trust3].map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check size={16} className="text-[#2C4C3B]" strokeWidth={1.75} /> {pick(t, lang)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Court info */}
      <section className="section-y bg-[#EAE6DF]" data-testid="city-court">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">{isHi ? "उच्च न्यायालय" : "The High Court"}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight text-[#1A1A1A] mb-6" data-testid="city-court-h2">
              {courtName}
            </h2>
            <ul className="space-y-3 text-[#4A4640]">
              <li className="flex items-start gap-3">
                <Landmark size={16} className="text-[#2C4C3B] mt-1 flex-shrink-0" />
                <span>
                  <span className="text-[#736F66] text-sm block">{isHi ? "स्थान" : "Seat"}</span>
                  {c.seat}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Scale size={16} className="text-[#2C4C3B] mt-1 flex-shrink-0" />
                <span>
                  <span className="text-[#736F66] text-sm block">{isHi ? "क्षेत्राधिकार" : "Jurisdiction"}</span>
                  {c.jurisdiction}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="editorial-num" style={{ fontSize: "1.5rem", opacity: 1 }}>{c.established}</span>
                <span className="pt-1">
                  <span className="text-[#736F66] text-sm block">{isHi ? "स्थापित" : "Established"}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-7">
            <div className="overline mb-4">{isHi ? "अधीनस्थ न्यायालय जिन्हें हम कवर करते हैं" : "Subordinate courts we cover"}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {c.subordinateCourts.map((court, i) => (
                <div
                  key={i}
                  className="bg-white border border-[#D8CDBA] rounded-2xl px-5 py-4 text-sm text-[#1A1A1A] flex items-start gap-3"
                  data-testid={`city-court-${i}`}
                >
                  <Landmark size={14} className="text-[#C46B2F] mt-1 flex-shrink-0" strokeWidth={1.75} />
                  {court}
                </div>
              ))}
            </div>
            {c.landmarks && c.landmarks.length > 0 && (
              <div className="mt-8 text-sm text-[#736F66]">
                {isHi ? "आस-पास के स्थल: " : "Nearby: "}
                <span className="text-[#4A4640]">{c.landmarks.join(" · ")}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cases we handle (bail focus) */}
      <section className="section-y" data-testid="city-cases">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-4">{isHi ? "हम कैसे मदद करते हैं" : "How we help in " + c.city}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium leading-tight">
              {isHi
                ? `${cityName} में तीन प्रकार की ज़मानत — तीनों तेज़।`
                : `Three kinds of bail in ${c.city} — all moved fast.`}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: pick(T.cases.items.bail.points.regular, lang), n: "01" },
              { t: pick(T.cases.items.bail.points.anticipatory, lang), n: "02" },
              { t: pick(T.cases.items.bail.points.interim, lang), n: "03" },
            ].map((p, i) => (
              <div key={i} className="bg-white border border-[#D8CDBA] rounded-3xl p-8" data-testid={`city-bail-type-${i}`}>
                <div className="editorial-num mb-4" style={{ fontSize: "3rem" }}>{p.n}</div>
                <p className="text-[#4A4640] leading-relaxed">{p.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Understand before you hire — guide links */}
      <section className="section-y bg-[#FAF9F6]" data-testid="city-guides">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-4">{isHi ? "वकील रखने से पहले समझिए" : "Understand before you hire"}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium leading-tight" data-testid="city-guides-h2">
              {isHi ? "चार गाइड्स — साफ भाषा में, बिना डराए।" : "Four guides — plain language, no fear."}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ALL_GUIDES.map((g) => (
              <Link
                key={g.slug}
                to={`/guide/${g.slug}`}
                className="group bg-white border border-[#D8CDBA] rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                data-testid={`city-guide-${g.slug}`}
              >
                <BookOpen size={16} className="text-[#C46B2F] mb-3" strokeWidth={1.75} />
                <div className="font-serif-display text-lg font-medium text-[#1A1A1A] leading-snug group-hover:text-[#2C4C3B] transition-colors">
                  {pick(g.title, lang)}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* From the Rihai Journal — posts relevant to this city */}
      {posts.length > 0 && (
        <section className="section-y" data-testid="city-journal">
          <div className="container-x">
            <div className="max-w-2xl mb-10">
              <div className="overline mb-4">{isHi ? "Rihai जर्नल से" : "From the Rihai Journal"}</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-medium leading-tight" data-testid="city-journal-h2">
                {isHi ? `${cityName} के परिवारों के लिए लिखा गया।` : `Written for families in ${c.city}.`}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group bg-white border border-[#D8CDBA] rounded-2xl p-6 hover:border-[#2C4C3B] transition-colors"
                  data-testid={`city-post-${p.id}`}
                >
                  <div className="text-xs uppercase tracking-widest text-[#C46B2F] mb-2">{p.category}</div>
                  <div className="font-serif-display text-xl font-medium text-[#1A1A1A] leading-snug mb-3 group-hover:text-[#2C4C3B] transition-colors">
                    {decodeHtml(p.title)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#736F66]">
                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.read_time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local FAQ */}
      <section className="section-y bg-[#FAF9F6]" data-testid="city-faq">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline mb-4">{isHi ? cityName + " के सवाल" : cityName + " questions"}</div>
            <h2 className="font-serif-display text-4xl font-medium leading-tight">
              {isHi ? "स्थानीय जवाब, साफ भाषा में।" : "Local answers, plain language."}
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {c.localFAQ.map((f, i) => (
                <AccordionItem key={i} value={`city-q-${i}`} className="border-b border-[#D8CDBA]">
                  <AccordionTrigger className="text-left font-serif-display text-xl md:text-2xl font-medium hover:no-underline py-5" data-testid={`city-faq-q-${i}`}>
                    {isHi ? f.qHi : f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A4640] text-base leading-relaxed pb-6" data-testid={`city-faq-a-${i}`}>
                    {isHi ? f.aHi : f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-y bg-[#2C4C3B] text-white relative overflow-hidden" data-testid="city-closing">
        <div className="blob" style={{ width: 400, height: 400, background: "#E2762A", top: -120, right: -80, opacity: 0.2 }} />
        <div className="container-x relative">
          <div className="tricolor-band tricolor-band-thick mb-8 max-w-[120px]" />
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight mb-5 max-w-3xl">
            {isHi
              ? `${cityName} में एक भरोसेमंद वकील। एक कॉल दूर।`
              : `A trusted lawyer in ${c.city}. One call away.`}
          </h2>
          <p className="text-[#D8CDBA] mb-10 max-w-xl">
            {isHi
              ? "एक फॉर्म भरिए, या अभी फ़ोन कीजिए। बीच में कोई नहीं।"
              : "Fill one form, or call us right now. No middlemen in between."}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/#lead" className="btn-primary bg-white text-[#2C4C3B] hover:bg-[#EAE6DF]" data-testid="city-closing-lead">
              {pick(T.hero.ctaPrimary, lang)} <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${T.phone.number}`}
              className="btn-secondary border-white text-white hover:bg-white hover:text-[#2C4C3B]"
              data-testid="city-closing-call"
            >
              <Phone size={16} strokeWidth={2} /> {T.phone.display}
            </a>
          </div>
        </div>
      </section>

      {/* Nearby cities served — cross-linking module */}
      <section className="py-12 border-t border-[#D8CDBA]" data-testid="city-nearby">
        <div className="container-x">
          <div className="overline mb-4">
            {isHi ? "अन्य शहर जहाँ हम सेवा देते हैं" : "Nearby cities we serve"}
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_CITIES.filter((x) => x.slug !== c.slug).map((x) => (
              <Link
                key={x.slug}
                to={`/${x.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#D8CDBA] px-4 py-2 text-sm text-[#4A4640] hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors"
                data-testid={`city-nearby-${x.slug}`}
              >
                <MapPin size={12} strokeWidth={2} />
                {isHi ? `ज़मानत वकील ${x.cityHindi}` : `Bail lawyer in ${x.city}`}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
